import React, { useState, useContext } from 'react';
import './PasswordChange.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

export const checkPasswordStrength = (password: string, t?: (key: string) => string): string => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);

    if (password.length < minLength || !hasUpperCase || !hasLowerCase || !hasNumber) {
        return t ? t('password.strength_error') : 'Das neue Passwort muss mindestens 8 Zeichen lang sein, sowie einen Großbuchstaben, einen Kleinbuchstaben und eine Zahl enthalten.';
    }
    return ''; // Password is strong enough
};

const PasswordChangeComponent: React.FC = () => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const authContext = useContext(AuthContext);
    const { t } = useTranslation();

    if (!authContext) {
        throw new Error('PasswordChangeComponent must be used within an AuthProvider');
    }

    const { token } = authContext;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setErrorMessage(t('password.error_mismatch'));
            return;
        }

        try {
            const response = await fetch('/api/users/me/password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    oldPassword: currentPassword,
                    newPassword: newPassword,
                }),
            });

            if (response.ok) {
                toast.success(t('password.success'));
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setErrorMessage('');
            } else {
                const errorData = await response.json();
                setErrorMessage(errorData.message || t('password.error_generic'));
                toast.error(errorData.message || t('password.error_generic'));
            }
        } catch (err) {
            setErrorMessage(t('password.error_generic'));
            toast.error(t('password.error_generic'));
        }
    };

    const isFormValid = currentPassword.trim() !== '' && newPassword.trim() !== '' && confirmPassword.trim() !== '';
    const calculateErrorMessage = () => {
        if (!isFormValid) {
            return '';
        }
        if (newPassword !== confirmPassword) {
            return t('password.error_mismatch');
        }
        return checkPasswordStrength(newPassword, t);
    };
    const displayedError = errorMessage ? errorMessage : calculateErrorMessage();
    const buttonEnabled = isFormValid && !displayedError;

    return (
        <div>
            <h3>{t('password.title')}</h3>
            <form className="password-change-form" onSubmit={handleSubmit}>
                {displayedError && <p className="error-message">{displayedError}</p>}
                <div className="form-group">
                    <label htmlFor="current-password">{t('password.current')}</label>
                    <input
                        type="password"
                        id="current-password"
                        name="current-password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="new-password">{t('password.new')}</label>
                    <input
                        type="password"
                        id="new-password"
                        name="new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="confirm-password">{t('password.confirm')}</label>
                    <input
                        type="password"
                        id="confirm-password"
                        name="confirm-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                    />
                </div>
                <button type="submit" disabled={!buttonEnabled}>
                    {t('password.button')}
                </button>
            </form>
        </div>
    );
};

export default PasswordChangeComponent;